// externals
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BreakPoint, logNewRelicPageAction } from '@makemydeal/dr-activities-common';

// libraries
import { dealerSelectors, selectedOfferSelectors } from '@makemydeal/dr-shared-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';

import * as appSelectors from '@makemydeal/dr-dash-store/selectors/appSelectors';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import * as deskingActionCreators from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import * as dueAtSigningSelectors from '@makemydeal/dr-dash-store/selectors/dueAtSigningSelectors';
import * as navigationActionCreators from '@makemydeal/dr-dash-store/actions/navigationActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as printPdfActionCreator from '@makemydeal/dr-dash-store/actions/printPdfActionCreator';
import * as tradeInCardSelectors from '@makemydeal/dr-dash-store/selectors/tradeInCardSelectors';
import * as unsavedChangesSelectors from '@makemydeal/dr-dash-store/selectors/unsavedChangesSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { useDealCommentsPanel } from '@makemydeal/dr-shared-components';

// components
import { Button } from '@interstate/components/Button';
import type { MenuReferenceType } from '@interstate/components/Menu';
import { Menu } from '@interstate/components/Menu';
import { Snackbar } from '@interstate/components/Snackbar';

// styling
import { ChatBubbleLeftRightIcon, ChevronDownIcon } from '@interstate/components/Icons';
import { StatusIndicator } from '@interstate/components/StatusIndicator';
import { useMediaQuery } from 'react-responsive';
import {
    Action,
    DAS_SUCCESS_MESSAGE,
    DEAL_REVIEW_AND_SUBMISSION_DIALOG_HEADER,
    DEAL_REVIEW_AND_SUBMISSION_DIALOG_PROMPT
} from '../constants';
import { useCustomerUI } from '../customerUI/useCustomerUI';
import DealComments from '../dealComments/DealComments';
import { TemplateButtonsBarWithBuilderMode } from '../dealTemplates/dealTemplatesDrawer/TemplateButtonsBarWithBuilderMode';
import { PrintPdfLoadingModal } from '../printPdfLoadingModal/PrintPdfLoadingModal';
import ReadyToSendDialogInterstate from '../readyToSendDialog/ReadyToSendDialog.interstate';
import { ShareToCustomerDialogInterstate } from '../ShareToCustomerDialog/ShareToCustomerDialog.interstate';
import { ShareToSalesViewDialog } from '../ShareToSalesView/ShareToSalesViewDialog.interstate';
import { UpdateDraftDialog } from '../UpdateOfferDialog/UpdateDraftDialog';
import { UpdateOfferDialog } from '../UpdateOfferDialog/UpdateOfferDialog';
import { useValidationGuard } from '../hooks/useValidationGuard';
const actionCreatorMap = {
    [Action.CONTINUE_IN_DESKING]: deskingActionCreators.continueInDesking,
    [Action.SEND_TO_ECOMMERCE]: deskingActionCreators.sendToEcommerce
} as const;

const readyToSendDialogContentMap = {
    [Action.CONTINUE_IN_DESKING]: {
        dialogHeader: 'Ready to Continue in Desking',
        dialogPrompt: 'Clicking "Yes" will update the offer and open Connect Desking. Are you sure you want to continue?'
    },
    [Action.SEND_TO_ECOMMERCE]: {
        dialogHeader: 'Ready to Send to Ecommerce?',
        dialogPrompt: 'Clicking "Yes" will send you to Ecommerce. Are you sure you want to continue?'
    }
} as const;

enum ShareActionTypes {
    SHARE_TO_SHOW_ROOM = 'ShareToShowroom',
    GET_DEAL_LINK = 'GetDealLink',
    SHARE_TO_CUSTOMER = 'ShareToCustomer',
    UPDATE = 'UPDATE',
    PRINT = 'PRINT',
    DRAFT = 'DRAFT'
}

// Reusable toggler for boolean state updates
// callback not executed by this component, ignore coverage
// istanbul ignore next
const toggler = (prev: boolean) => !prev;

const ActionsInterstate = () => {
    const [showReadyToSendDialog, setShowReadyToSendDialog] = useState(false);
    const [showReadyToSendDialogSave, setShowReadyToSendDialogSave] = useState(false);

    const [showUpdateOffer, setShowUpdateOffer] = useState(false);
    const [showSaveDraft, setShowSaveDraft] = useState(false);

    const [showUpdateSuccessSnackbar, setUpdateSuccessSnackbar] = useState(false);
    const [showDraftSuccessSnackbar, setSaveDraftSuccessNotification] = useState(false);
    const [showDraftFailureSnackbar, setSaveDraftFailureNotification] = useState(false);

    // DAS success notification state
    const [showDasSuccessSnackbar, setShowDasSuccessSnackbar] = useState(false);
    const [isDasApplyInProgress, setIsDasApplyInProgress] = useState(false);
    const [dasApplySuccessful, setDasApplySuccessful] = useState(false);
    // callback not executed by this component, ignore coverage
    // istanbul ignore next
    const toggleUpdateSuccessSnackbar = () => setUpdateSuccessSnackbar(toggler);
    // callback not executed by this component, ignore coverage
    // istanbul ignore next
    const toggleDraftSuccessSnackbar = () => setSaveDraftSuccessNotification(toggler);
    // callback not executed by this component, ignore coverage
    // istanbul ignore next
    const toggleDraftFailureSnackbar = () => setSaveDraftFailureNotification(toggler);

    const [currentAction, setCurrentAction] = useState<Action>(Action.CONTINUE_IN_DESKING);
    const actionsEnabled = useSelector(appSelectors.isLazyInitCompleteForSmartInit);
    const dealExchangeDealId = useSelector(selectedOfferSelectors.getSelectedOfferDealXgId);
    const initDealExchangeDealVersion = useSelector(selectedOfferSelectors.getSelectedOfferDealXgVersion);
    const latestDealExchangeVersion = useSelector(offerInfoSelectors.getDealXgVersion);
    const copyToDeskingURL = useSelector(configSelectors.getCopyToDeskingURL);
    const isEcommerceDeal = useSelector(dealXgDetailsSelectors.getIsEcommerceDeal);

    const redirectionLink = useSelector(dealXgDetailsSelectors.getRedirectionLink);
    const offerIsBeingSaved = useSelector(offerInfoSelectors.getIsOfferBeingSaved);
    const offerHasBeenSaved = useSelector(offerInfoSelectors.getHasBeenSaved);
    const draftIsBeingSaved = useSelector(offerInfoSelectors.getIsDraftBeingSaved);
    const draftHasBeenSaved = useSelector(offerInfoSelectors.getHasDraftBeenSaved);
    const [showShareToSalesViewDialog, setShowShareToSalesViewDialog] = useState(false);
    const [showShareToCustomerDialog, setShowShareToCustomerDialog] = useState(false);
    const [showGetDealLinkDialog, setShowGetDealLinkDialog] = useState(false);
    const [showShareDropdown, setShareDropdown] = useState<null | HTMLElement>(null);
    const openShareDropdown = Boolean(showShareDropdown);
    const crmIntegrationToggle = useSelector(dealerSelectors.getCrmIntegrationToggle);
    const { showCustomerCard } = useCustomerUI();
    const largerThanSM = useMediaQuery({ query: `(min-width: ${BreakPoint.SMALL})` });
    const isContinueToDeskingDisabled = useSelector(dealerSelectors.isContinueToDeskingDisabled);
    const isLatestPushToDmsPlusEnabled = useSelector(dealXgDetailsSelectors.getLatestPushToDmsPlus);
    const sessionId = useSelector(offerReduxSelectors.getSessionId);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const dealXgVersion = useSelector(offerReduxSelectors.getDealXgVersion);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const vin = useSelector(vehicleSelectors.getVehicleVin);
    const creditDecisionEnabled = useSelector(offerReduxSelectors.getCreditDecisionEnabled);
    const includesTradeIn = useSelector(tradeInCardSelectors.getShouldIncludeTradeIn);
    const scopesOrder = useSelector(scopedStateSelectors.getScopesOrder);
    const showSaveDraftButton = useSelector(unsavedChangesSelectors.currentOfferHasUnsavedChanges);
    const isDealTemplatesViewEnabled = useSelector(configSelectors.isDealTemplatesViewEnabled);
    const enableDealComments = useSelector(dealerSelectors.isDealCommentsMVEnabled);
    const commonOrgId = useSelector(offerReduxSelectors.getCommonOrgId);
    const dealComments = useDealCommentsPanel({ isEnabled: enableDealComments, referenceId: dealXgId, commonOrgId });
    const isDealAuditLog = useSelector(dealerSelectors.isDealAuditLog);

    // DAS selectors
    const isDasApplied = useSelector(dueAtSigningSelectors.getIsApplied);
    const isDasCalculating = useSelector(dueAtSigningSelectors.getIsCalculating);
    const showDasSlideOut = useSelector(dueAtSigningSelectors.getIsSlideOutOpen);

    // istanbul ignore next # this is invoked WAY down the pipeline and out of scope here.
    const urlSelector = (state: any) => {
        const url = offerInfoSelectors.getShopperURL(state);
        return `\n${url}`;
    };
    const dispatch = useDispatch();
    const guard = useValidationGuard();

    const dispatchAction = useCallback(
        (action: Action) => {
            const actionCreator = actionCreatorMap[action];
            if (actionCreator) {
                dispatch(actionCreator());
            }
        },
        [dispatch]
    );

    const handleActionClick = useCallback(
        (action: Action) => {
            setCurrentAction(action);
            setShowReadyToSendDialog(true);
            dispatchAction(action);
        },
        [setCurrentAction, setShowReadyToSendDialog, dispatchAction]
    );

    // NOTE: Ignoring coverage on the next lines because these are dependent on already asserted code in sub-components tests
    // We ARE asserting coverage on actionable behaviors like: (handleSendToCustomerClick, handleContinueInDeskingClick)

    // istanbul ignore next
    const closeReadyToSendDialog = useCallback(() => {
        setShowReadyToSendDialog(false);
    }, [setShowReadyToSendDialog]);

    const handleContinueToDeskingSaved = useCallback(() => {
        closeReadyToSendDialog();
        window.open(copyToDeskingURL + dealExchangeDealId, '_blank');
    }, [closeReadyToSendDialog, copyToDeskingURL, dealExchangeDealId]);

    const handleSendToEcommerceSaved = useCallback(() => {
        closeReadyToSendDialog();
        const dealExchangeVersion = latestDealExchangeVersion || initDealExchangeDealVersion;
        window.location.assign(`${redirectionLink}/${dealExchangeDealId}/version/${dealExchangeVersion}`);
    }, [closeReadyToSendDialog, latestDealExchangeVersion, initDealExchangeDealVersion, redirectionLink, dealExchangeDealId]);

    const handleReadyToSendSaved = useCallback(() => {
        switch (currentAction) {
            case Action.CONTINUE_IN_DESKING:
                return handleContinueToDeskingSaved();
            case Action.SEND_TO_ECOMMERCE:
                return handleSendToEcommerceSaved();
        }
    }, [currentAction, handleContinueToDeskingSaved, handleSendToEcommerceSaved]);

    const readyToSendDialogHeader =
        currentAction === Action.SEND_TO_ECOMMERCE
            ? DEAL_REVIEW_AND_SUBMISSION_DIALOG_HEADER
            : readyToSendDialogContentMap[currentAction].dialogHeader;
    const readyToSendDialogPrompt =
        currentAction === Action.SEND_TO_ECOMMERCE
            ? DEAL_REVIEW_AND_SUBMISSION_DIALOG_PROMPT
            : readyToSendDialogContentMap[currentAction].dialogPrompt;

    // istanbul ignore next
    const handleShareButtonClick = useCallback(
        (e: any) => {
            setShareDropdown(e.currentTarget);
        },
        [setShareDropdown]
    );

    // istanbul ignore next
    const handleShareButtonClose = useCallback(() => setShareDropdown(null), [setShareDropdown]);

    const handleSaveRequest = useCallback(
        guard.wrap((actionType: ShareActionTypes) => {
            switch (actionType) {
                case ShareActionTypes.SHARE_TO_CUSTOMER:
                    dispatch(offerActionCreators.saveOffer());
                    handleShareButtonClose();
                    if (crmIntegrationToggle) {
                        showCustomerCard(true, { tab: 'inbox', messageContentSelector: urlSelector, waitForOfferSave: true });
                    } else {
                        setShowShareToCustomerDialog(true);
                    }
                    break;
                case ShareActionTypes.GET_DEAL_LINK:
                    dispatch(offerActionCreators.saveOffer());
                    setShowGetDealLinkDialog(true);
                    handleShareButtonClose();
                    break;
                case ShareActionTypes.SHARE_TO_SHOW_ROOM:
                    setShowShareToSalesViewDialog(true);
                    handleShareButtonClose();
                    break;
                case ShareActionTypes.UPDATE:
                    dispatch(offerActionCreators.saveOffer());
                    setShowUpdateOffer(true);
                    break;
                case ShareActionTypes.DRAFT:
                    dispatch(offerActionCreators.saveOffer({ isDraft: true }));
                    setShowSaveDraft(true);
                    break;
            }
        }),
        [dispatch, crmIntegrationToggle, handleShareButtonClose, showCustomerCard, guard.wrap]
    );

    const handlePrintRequest = useCallback(() => {
        dispatch(offerActionCreators.saveOffer());
        dispatch(printPdfActionCreator.printCustomizablePdf());
        handleShareButtonClose();
    }, [dispatch, handleShareButtonClose]);

    // NOTE: Setup menu options; Sales view will always show. Toggling behavior of share to customer and get deal link is
    // based on OCC toggle and CRM Integration settings.

    const options = useMemo(
        () => [
            {
                label: 'Share to Customer',
                value: ShareActionTypes.SHARE_TO_CUSTOMER,
                id: 'share-to-customer-option',
                onSelect: () => {
                    handleSaveRequest(ShareActionTypes.SHARE_TO_CUSTOMER);
                    dispatch(deskingActionCreators.sendToCustomerClicked());
                }
            },
            {
                label: 'Share to Sales View',
                value: ShareActionTypes.SHARE_TO_SHOW_ROOM,
                id: 'share-to-showroom-option',
                onSelect: () => {
                    handleSaveRequest(ShareActionTypes.SHARE_TO_SHOW_ROOM);
                }
            },
            {
                label: 'Get Deal Link',
                value: ShareActionTypes.GET_DEAL_LINK,
                id: 'get-deal-link-option',
                divider: true,
                onSelect: () => {
                    handleSaveRequest(ShareActionTypes.GET_DEAL_LINK);
                }
            },
            {
                label: 'Print',
                value: ShareActionTypes.PRINT,
                id: 'print-option',
                onSelect: () => {
                    handlePrintRequest();
                }
            }
        ],
        [handleSaveRequest, handlePrintRequest]
    );

    const logNRAction = useCallback(
        (name: string) => {
            logNewRelicPageAction(name, {
                sessionId,
                dealXgId,
                dealXgVersion,
                dealerId,
                vin,
                creditDecisionEnabled,
                includesTradeIn,
                dealsCount: scopesOrder.length
            });
        },
        [creditDecisionEnabled, dealXgId, dealXgVersion, dealerId, includesTradeIn, scopesOrder.length, sessionId, vin]
    );

    const confirmAndSaveRequest = useCallback(() => {
        if (isLatestPushToDmsPlusEnabled) {
            setShowReadyToSendDialogSave(true);
        } else {
            handleSaveRequest(ShareActionTypes.UPDATE);
        }
        logNRAction('MV:Multiscenario - Update deal');
    }, [isLatestPushToDmsPlusEnabled, setShowReadyToSendDialogSave, handleSaveRequest, logNRAction]);

    // Note: Cancel order logic is now handled globally in offerMiddleware.ts
    // The middleware will automatically check if cancellation is needed before any save operation
    const handleUpdateClick = useCallback(() => {
        confirmAndSaveRequest();
    }, [confirmAndSaveRequest]);

    const handleSave = useCallback(() => {
        setShowReadyToSendDialogSave(false);
    }, [setShowReadyToSendDialogSave]);

    useEffect(() => {
        // istanbul ignore next
        if (!showUpdateOffer) return;

        // istanbul ignore next
        if (offerIsBeingSaved === false && offerHasBeenSaved === true) {
            setUpdateSuccessSnackbar(true);
            setShowUpdateOffer(false);
            return;
        }
    }, [offerIsBeingSaved, offerHasBeenSaved]);
    useEffect(() => {
        if (!showSaveDraft) return;
        if (draftIsBeingSaved === false && draftHasBeenSaved === true) {
            setShowSaveDraft(false);
            toggleDraftSuccessSnackbar();
        } else if (draftIsBeingSaved === false && draftHasBeenSaved === false) {
            setShowSaveDraft(false);
            toggleDraftFailureSnackbar();
        }
    }, [draftIsBeingSaved, draftHasBeenSaved, showSaveDraft]);

    useEffect(() => {
        if (isDasCalculating && !isDasApplyInProgress) {
            setIsDasApplyInProgress(true);
        }

        // istanbul ignore next
        if (!isDasCalculating) {
            if (isDasApplyInProgress) {
                setIsDasApplyInProgress(false);
            } else {
                if (isDasApplied) {
                    setDasApplySuccessful(true);
                } else if (dasApplySuccessful) {
                    setDasApplySuccessful(false);
                }
            }
        }
    }, [isDasApplied, isDasCalculating, isDasApplyInProgress]);

    useEffect(() => {
        // Show success message only when slideout closes after successful apply
        // istanbul ignore next
        if (dasApplySuccessful && !showDasSlideOut) {
            setShowDasSuccessSnackbar(true);
            setDasApplySuccessful(false);
        }
    }, [dasApplySuccessful, showDasSlideOut]);

    const handleContinueInDeskingClick = useCallback(() => handleActionClick(Action.CONTINUE_IN_DESKING), [handleActionClick]);
    const handleSendToEcommerceClick = useCallback(() => handleActionClick(Action.SEND_TO_ECOMMERCE), [handleActionClick]);

    const setShowReadyToSendDialogSaveOnNoOrHide = useCallback(
        () => setShowReadyToSendDialogSave(false),
        [setShowReadyToSendDialogSave]
    );

    const handleSaveDraftClick = () => {
        handleSaveRequest(ShareActionTypes.DRAFT);
        logNRAction('MV:Multiscenario - Save draft');
    };

    const handleOpenDealMentions = () => {
        dealComments.open();
        logNRAction('MV:DealComments - Panel Opened');
    };

    const handleCloseDealMentions = () => {
        dealComments.close();
        logNRAction('MV:DealComments - Panel Closed');
    };

    // NOTE: next 3 callbacks have been extracted as a part of perf optimization and aren't covered by tests
    // istanbul ignore next
    const updateOfferDialogOnHideCallback = useCallback(() => setShowUpdateOffer(false), [setShowUpdateOffer]);

    // istanbul ignore next
    const shareToCustomeDialogOnHide = useCallback(() => setShowGetDealLinkDialog(false), [setShowGetDealLinkDialog]);
    const shareToCustomerDialogOnHide = useCallback(() => setShowShareToCustomerDialog(false), [setShowShareToCustomerDialog]);
    const shareToSalesViewOnHideCallback = useCallback(() => setShowShareToSalesViewDialog(false), [setShowShareToSalesViewDialog]);
    const shareToSalesViewOnOfferSaveHide = useCallback(() => {
        setUpdateSuccessSnackbar(true);
        setShowShareToSalesViewDialog(false);
    }, [setUpdateSuccessSnackbar, setShowShareToSalesViewDialog]);

    const navigateToAudit = useCallback(() => {
        dispatch(navigationActionCreators.navigateToAuditLog());
    }, [dispatch]);

    // istanbul ignore next
    const dasSuccessSnackbarOnCloseCallback = useCallback(() => setShowDasSuccessSnackbar(false), [setShowDasSuccessSnackbar]);

    if (isDealTemplatesViewEnabled) {
        return <TemplateButtonsBarWithBuilderMode disabled={!actionsEnabled} />;
    }

    return (
        <>
            <div className="desking-btn-container">
                {!isContinueToDeskingDisabled && (
                    <Button
                        data-testid="action-copy-to-desking"
                        className="continue-desking-button"
                        size="medium"
                        buttonStyle="tertiary"
                        disabled={!actionsEnabled}
                        sx={{ fontWeight: 400 }}
                        onClick={handleContinueInDeskingClick}
                    >
                        Continue in Desking
                    </Button>
                )}
                {isEcommerceDeal && (
                    <Button
                        data-testid="action-send-to-ecommerce"
                        size="medium"
                        disabled={!actionsEnabled}
                        buttonStyle="tertiary"
                        onClick={handleSendToEcommerceClick}
                    >
                        Deal Review & Submission
                    </Button>
                )}
                {isDealAuditLog && (
                    <Button
                        data-testid="action-nav-to-audit-log"
                        size="medium"
                        disabled={!actionsEnabled}
                        buttonStyle="tertiary"
                        onClick={navigateToAudit}
                    >
                        Audit Log
                    </Button>
                )}
            </div>
            {enableDealComments && (
                <div data-testid="deal-comments-button-container">
                    <StatusIndicator
                        indicatorContent={dealComments.showRedDot ? ('!' as any) : undefined}
                        variant="standard"
                        className="deal-comments-button"
                    >
                        <Button
                            data-testid="action-open-deal-mentions"
                            disabled={false}
                            buttonStyle="secondary"
                            onClick={handleOpenDealMentions}
                            icon={<ChatBubbleLeftRightIcon />}
                        />
                    </StatusIndicator>
                    <DealComments
                        isOpen={dealComments.isOpen}
                        commentsElementRef={dealComments.commentsElementRef}
                        onClose={handleCloseDealMentions}
                        styleVariant="deal-comments-footer"
                    />
                </div>
            )}

            <div className="share-container-buttons">
                {showSaveDraftButton && (
                    <Button
                        data-testid="save-draft"
                        className="continue-desking-button"
                        size="medium"
                        buttonStyle="tertiary"
                        disabled={!actionsEnabled}
                        sx={{ fontWeight: 400 }}
                        onClick={handleSaveDraftClick}
                    >
                        Save Draft
                    </Button>
                )}
                <Button
                    endIcon={<ChevronDownIcon />}
                    buttonStyle="secondary"
                    disabled={!actionsEnabled}
                    className="share-dropdown-button"
                    data-testid="share-dropdown"
                    onClick={handleShareButtonClick}
                >
                    Share
                </Button>
                <Menu
                    referenceProps={{
                        open: openShareDropdown,
                        anchorEl: showShareDropdown as MenuReferenceType,
                        onClose: handleShareButtonClose
                    }}
                    position={'top-end'}
                    menuOptions={options}
                    width={largerThanSM ? '185px' : '100%'}
                />
                <Button data-testid="update-offer-button" size="medium" disabled={!actionsEnabled} onClick={handleUpdateClick}>
                    Update
                </Button>
            </div>

            <ShareToSalesViewDialog
                show={showShareToSalesViewDialog}
                onHide={shareToSalesViewOnHideCallback}
                onOfferSaveHide={shareToSalesViewOnOfferSaveHide}
            />
            <ShareToCustomerDialogInterstate
                prefix="share-to-customer"
                show={showShareToCustomerDialog}
                onHide={shareToCustomerDialogOnHide}
                header="Share To Customer"
                dialogPrompt="Choose how you would like to send this offer to the shopper"
            />
            <ShareToCustomerDialogInterstate
                prefix="get-deal-link"
                show={showGetDealLinkDialog}
                onHide={shareToCustomeDialogOnHide}
                header="Get Deal Link"
            />
            <PrintPdfLoadingModal />
            <ReadyToSendDialogInterstate
                data-testid="ready-to-send-dialog"
                dialogHeader={readyToSendDialogHeader}
                dialogPrompt={readyToSendDialogPrompt}
                show={showReadyToSendDialog}
                onHide={closeReadyToSendDialog}
                onNo={closeReadyToSendDialog}
                onSaved={handleReadyToSendSaved}
                promptSx={{ padding: '0px' }}
                action={currentAction}
            />
            {!showReadyToSendDialog && (
                <ReadyToSendDialogInterstate
                    data-testid="ready-to-send-save-dialog"
                    dialogHeader={'Warning'}
                    dialogPromptHeader={'Important Note:'}
                    dialogPrompt={`Pushing again will overwrite the existing deal in your DMS.`}
                    show={showReadyToSendDialogSave}
                    onHide={setShowReadyToSendDialogSaveOnNoOrHide}
                    onNo={setShowReadyToSendDialogSaveOnNoOrHide}
                    onSaved={handleSave}
                    promptSx={{
                        paddingLeft: '0',
                        b: {
                            paddingRight: '5px'
                        }
                    }}
                />
            )}

            <UpdateOfferDialog
                show={showUpdateOffer}
                onHide={updateOfferDialogOnHideCallback}
                onSuccess={toggleUpdateSuccessSnackbar}
            />
            <Snackbar
                data-testid="update-offer-dialog-success"
                show={showUpdateSuccessSnackbar}
                message="We've updated your changes to this offer."
                position="bottom-center"
                onClose={toggleUpdateSuccessSnackbar}
                type="success"
            />
            <UpdateDraftDialog show={showSaveDraft} />
            <Snackbar
                data-testid="update-draft-dialog-success"
                show={showDraftSuccessSnackbar}
                message="Successfully Saved as Draft!"
                position="bottom-center"
                onClose={toggleDraftSuccessSnackbar}
                type="success"
            />
            <Snackbar
                data-testid="update-draft-dialog-failure"
                show={showDraftFailureSnackbar}
                message="Unable to Save Draft. Please Try Again"
                position="bottom-center"
                onClose={toggleDraftFailureSnackbar}
                type="error"
            />
            <Snackbar
                data-testid="das-success-snackbar"
                show={showDasSuccessSnackbar}
                message={DAS_SUCCESS_MESSAGE}
                position="bottom-center"
                onClose={dasSuccessSnackbarOnCloseCallback}
                type="success"
            />
        </>
    );
};
export default ActionsInterstate;
